import { useState } from 'react';
import type { FactoryIdentityController } from './useFactoryIdentity';
import { FactoryIcon } from './FactoryPrimitives';
import FactoryIdentityQr from './FactoryIdentityQr';
import './FactoryIdentityPanel.css';

const labels = { unverified: '未认证', pending: '认证中', verified: '已认证', failed: '未通过', expired: '已过期' };

export default function FactoryIdentityPanel({ identity }: { identity: FactoryIdentityController }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [certNo, setCertNo] = useState('');
  const [consent, setConsent] = useState(false);
  const { status, working, error } = identity;
  const close = () => { setOpen(false); setName(''); setCertNo(''); setConsent(false); };
  const submit = async () => {
    await identity.start(name, certNo);
    // Sensitive input is never persisted in drafts, browser storage or URLs.
    setName(''); setCertNo(''); setConsent(false); setOpen(false);
  };
  return <section className="fx-card fx-identity-card" aria-labelledby="factory-identity-heading">
    <div className="fx-card-heading">
      <h3 id="factory-identity-heading"><FactoryIcon name="lock" />实名认证</h3>
      <span className={`fx-badge ${status?.status === 'verified' ? 'green' : 'amber'}`}>
        {status ? labels[status.status] : '正在读取'}
      </span>
    </div>
    <p className="fx-muted">通过支付宝核验发布者本人身份。实名认证不代表专家资质审核，也不替代创建他人分身所需的授权。</p>
    {status?.status === 'verified' ? <p className="fx-identity-result"><FactoryIcon name="check" />发布者已实名：{status.masked_name} · {status.masked_cert_no}</p>
      : status?.required ? <p className="fx-fine">发布前需完成一次实名认证，同一账号下的智能体无需重复认证。</p>
        : status ? <p className="fx-fine">实名认证服务暂未开放。</p> : null}
    {status?.required && !status.available && <p className="fx-fine">实名认证服务暂不可用，请稍后重试或联系平台。</p>}
    {status?.status === 'pending' && <p className="fx-fine">请在支付宝内完成认证，返回后点击“查询认证结果”。</p>}
    {status?.status === 'failed' && <p className="fx-fine">暂未通过认证。若刚完成支付宝操作，请再次查询结果；重新发起前请核对本人信息。</p>}
    {error && <p className="fx-error" role="alert">{error}</p>}
    {identity.certifyUrl && status?.status !== 'verified' && status?.status !== 'expired' && <div className="fx-identity-handoff" role="group" aria-label="手机支付宝扫码认证">
      <h4>请用手机支付宝扫一扫</h4>
      <p>电脑浏览器无法直接完成人脸认证。打开本人手机支付宝，扫描下方二维码继续。</p>
      <FactoryIdentityQr url={identity.certifyUrl} />
      <p className="fx-fine">请勿分享此二维码。认证完成后，本页会自动更新结果，也可点击“查询认证结果”。</p>
    </div>}
    {open && status?.available && status.status !== 'verified' && <div className="fx-identity-form">
      <label className="fx-field">真实姓名<input aria-label="实名认证真实姓名" value={name} maxLength={64} autoComplete="off" onChange={(e) => setName(e.target.value)} disabled={working} /></label>
      <label className="fx-field">身份证号<input aria-label="实名认证身份证号" value={certNo} maxLength={18} autoComplete="off" spellCheck={false} onChange={(e) => setCertNo(e.target.value)} disabled={working} /></label>
      <p className="fx-fine">目前支持中国大陆居民身份证。请本人操作，并在支付宝内完成身份验证。</p>
      <details className="fx-identity-consent"><summary>实名认证告知</summary><p>为核验发布者身份和管理智能体发布，猴毛将你填写的姓名、身份证号提供给支付宝身份验证服务。认证所需的人脸等信息由支付宝在其页面处理。猴毛保存脱敏身份信息、认证结果、同意记录及必要的认证凭证，不保存完整身份证号、证件原图或人脸图像。相关记录在认证有效期间和必要审计期限内保留；你可通过平台账号服务申请查询、更正或删除。不同意不影响草稿编辑，需认证的发布功能将无法使用。</p></details>
      <label className="fx-identity-consent-check"><input type="checkbox" checked={consent} disabled={working} onChange={(e) => setConsent(e.target.checked)} />我已阅读实名认证告知，同意为上述用途向支付宝提供姓名、身份证号并进行身份验证。</label>
      <div className="fx-actions"><button type="button" className="fx-button fx-primary" disabled={working || !consent || name.trim().length < 2 || !/^\d{17}[\dXx]$/.test(certNo.trim())} onClick={() => void submit()}>{working ? '正在发起…' : '前往支付宝认证'}</button><button type="button" className="fx-button" disabled={working} onClick={close}>取消</button></div>
    </div>}
    <div className="fx-actions">
      {!open && !identity.certifyUrl && status?.required && status.available && status.status !== 'verified' && <button type="button" className="fx-button fx-primary" disabled={working} onClick={() => setOpen(true)}>{status.status === 'pending' ? '继续认证' : '开始实名认证'}</button>}
      {status?.status !== 'verified' && <button type="button" className="fx-button" disabled={working} onClick={() => void identity.refresh()}>{working ? '正在查询…' : status?.status === 'pending' || status?.status === 'failed' ? '查询认证结果' : '刷新认证状态'}</button>}
    </div>
  </section>;
}
